/* @flow */
/* eslint-disable react/no-string-refs */
import React, { Element as ReactElement } from 'react';
import Portal from 'react-portal';
import classnames from 'classnames/bind';

import s from './Tooltip.styl';
const cx = classnames.bind(s);

type TooltipBodyProps = {
  top: number,
  left: number,
  right: number,
  bottom: number,
  isOpen: boolean,
  minWidth: number,
  topAdjust: number,
  children?: ReactElement<any>,
  onMouseLeave: Function,
};

class TooltipBody extends React.Component {
  static defaultProps = {
    topAdjust: 10,
  };

  props: TooltipBodyProps;

  state = {
    height: 0,
  };

  /**
   * We need the component to be mounted before we get its rendered height so we
   * call setState immediately after mount. This is considered an anti-pattern,
   * but given the circumstances there is no way to get the height (which we
   * need) before the component has been rendered to the dom.
   */
  componentDidMount() {
    this.setState({ height: this.refs.main.offsetHeight }); // eslint-disable-line react/no-did-mount-set-state
  }

  render() {
    const { height } = this.state;
    const opacity = this.props.isOpen ? 1 : 0;
    const visibility = height ? 'visible' : 'hidden';
    const { minWidth } = this.props;
    let top = this.props.top - height - this.props.topAdjust;
    let { left } = this.props;

    // Don't let the box hit the right edge of the screen. Also provide 10px of
    // space beteween the box and the edge of the screen
    if (window.innerWidth - left <= minWidth) left = left - minWidth - 10;

    // Don't let the tooltip go off screen to the top.
    if (top - height < 0) top = top + height + 30; // 30 just seemed to work...

    return (
      <div
        onMouseLeave={this.props.onMouseLeave}
        style={{ minWidth }}
        className={cx('TooltipBody')}>
        <div
          ref='main'
          style={{
            opacity,
            top,
            left,
            visibility,
          }}
          className={cx('tooltipBody')}>
          {this.props.children}
        </div>
      </div>
    );
  }

}

type CacheShape = {
  top: number,
  left: number,
  bottom: number,
  right: number,
};

type TooltipState = {
  isOpen: boolean,
  cache: ?CacheShape,
  top: number,
  left: number,
  bottom: number,
  right: number,
};

type TooltipProps = {
  style?: Object,
  minWidth?: number,
  className?: string,
  children?: ReactElement<any>,
  recalculateOnHover?: boolean,
};

/**
 * NOTE: This used pure render mixing before we converted it to use class
 * syntax.
 */
export class Tooltip extends React.Component {

  static defaultProps = {
    minWidth: 200,
  };

  props: TooltipProps;

  state: TooltipState = {
    isOpen: false,
    cache: null,
    top: 0,
    left: 0,
    bottom: 0,
    right: 0,
  };

  /**
   * This is essentially a momization of the getBoundingClientRect DOM method.
   * We don't want to be interacting with the DOM every time the user hovers
   * this component unless its explicitly necessary. That being if it's part of
   * a non-static layout. For example, for tooltips that are part of dropdowns
   * or other moving components its important to recalculate the position
   * whenever the users hovers, but for most tooltips this is unecessary.
   *
   * NOTE: We intentionally do NOT cache a reference to the DOMRect object. We
   * only cache the numeric values that are of interest to us.
   *
   * @return {object}
   */
  getBoundingRect = () => {
    const { cache } = this.state;

    // If caching is enabled and we have the cache simply return it
    if (cache && !this.props.recalculateOnHover) return cache;

    const { top, left, bottom, right } = this.refs.icon.getBoundingClientRect();
    const result = { top, left, bottom, right };

    if (!cache) this.setState({ cache: result });

    return result;
  };

  handleMouseEnter = () => {
    const { top, left, bottom, right } = this.getBoundingRect();
    this.setState({
      top,
      left,
      bottom,
      right,
      isOpen: true,
    });
  };

  handleMouseLeave = () => {
    this.setState({ isOpen: false });
  };

  render() {
    return (
      <div
        onMouseLeave={this.handleMouseLeave}
        style={this.props.style}
        className={cx('Tooltip', this.props.className)}>
        <span className={cx('tooltipLabel')}>
          <i
            ref='icon'
            onMouseEnter={this.handleMouseEnter}
            className='fa fa-info-circle' />
        </span>
        <Portal isOpened={this.state.isOpen}>
          <TooltipBody
            onMouseLeave={this.handleMouseLeave}
            minWidth={this.props.minWidth}
            {...this.state}>
            {this.props.children}
          </TooltipBody>
        </Portal>
      </div>
    );
  }
}


/**
 * TooltipWrap is slightly different than tooltip in that it is meant to wrap an
 * existing Element and will not create one for you.
 *
 * NOTE: This API is a bit questionable since it goes against the original
 * Tooltip API of the children beeing rendered as the tooltip itself. There is
 * also quite a bit of shared logic here so this would be a prime candidate for
 * refactoring in the future.
 *
 * Usage:
 * <TooltipWrap tooltip={<span>This is a tooltip</span>}>
 *   <i className='fa fa-home'></i>
 * </TooltipWrap>
 */

type TooltipWrapProps = {
  children?: any,
  recalculateOnHover?: boolean,
  className?: string,
  style?: Object,
  minWidth?: number,
  tooltip?: any,
  topAdjust?: number,
};

export class TooltipWrap extends React.Component {

  static defaultProps = {
    minWidth: 200,
  };

  props: TooltipWrapProps;

  state: TooltipState = {
    isOpen: false,
    cache: null,
    top: 0,
    left: 0,
    bottom: 0,
    right: 0,
  };

  /**
   * This is essentially a momization of the getBoundingClientRect DOM method.
   * We don't want to be interacting with the DOM every time the user hovers
   * this component unless its explicitly necessary. That being if it's part of
   * a non-static layout. For example, for tooltips that are part of dropdowns
   * or other moving components its important to recalculate the position
   * whenever the users hovers, but for most tooltips this is unecessary.
   *
   * NOTE: We intentionally do NOT cache a reference to the DOMRect object. We
   * only cache the numeric values that are of interest to us.
   * @return {object}
   */
  getBoundingRect = () => {
    const { cache } = this.state;

    // If caching is enabled and we have the cache simply return it
    if (cache && !this.props.recalculateOnHover) return cache;

    const { top, left, bottom, right } = this.refs.target.getBoundingClientRect();
    const result = { top, left, bottom, right };

    if (!cache) this.setState({ cache: result });

    return result;
  };

  handleMouseEnter = () => {
    const { top, left, bottom, right } = this.getBoundingRect();
    this.setState({
      top,
      left,
      bottom,
      right,
      isOpen: true,
    });
  };

  handleMouseLeave = () => {
    this.setState({ isOpen: false });
  };

  render() {
    return (
      <div
        onMouseLeave={this.handleMouseLeave}
        style={this.props.style}
        className={cx('Tooltip', this.props.className)}>
        <span ref='target' onMouseEnter={this.handleMouseEnter}>
          {this.props.children && React.cloneElement(this.props.children)}
        </span> 
      <Portal isOpened={this.state.isOpen}>
          <TooltipBody
            topAdjust={this.props.topAdjust}
            onMouseLeave={this.handleMouseLeave}
            minWidth={this.props.minWidth}
            {...this.state}>
            {this.props.tooltip}
          </TooltipBody>
        </Portal>
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./web_modules/trustar/components/Tooltip.js